import type { Timestamps, SoftDeletable, PaginationParams, DateRangeFilter, SortParams } from "./common";

// ─── Enums ────────────────────────────────────────────────────────────────────

/**
 * Cold Crew specific expense categories.
 * Maps to UI labels via CATEGORY_LABELS below.
 */
export type ExpenseCategory =
  | "cafe"
  | "agua"
  | "vainilla"
  | "conservador"
  | "etiquetas"
  | "botellas"
  | "contenido_redes"
  | "otro";

/** Human-readable labels for each category (used in UI). */
export const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  cafe:             "Café",
  agua:             "Agua",
  vainilla:         "Vainilla",
  conservador:      "Conservador",
  etiquetas:        "Etiquetas",
  botellas:         "Botellas",
  contenido_redes:  "Contenido para Redes Sociales",
  otro:             "Otro",
};

export const CATEGORY_VALUES = Object.keys(CATEGORY_LABELS) as ExpenseCategory[];

export type ExpensePaymentMethod =
  | "efectivo"
  | "transferencia"
  | "tarjeta"
  | "cheque"
  | "otro";

export const PAYMENT_METHOD_LABELS: Record<ExpensePaymentMethod, string> = {
  efectivo:      "Efectivo",
  transferencia: "Transferencia",
  tarjeta:       "Tarjeta",
  cheque:        "Cheque",
  otro:          "Otro",
};

// ─── Expense ──────────────────────────────────────────────────────────────────

export interface Expense extends Timestamps, SoftDeletable {
  id:          number;
  fecha:       string;                    // ISO date
  categoria:   ExpenseCategory;
  descripcion: string;
  monto:       number;
  metodo_pago: ExpensePaymentMethod;
  proveedor:   string | null;
  comprobante: string | null;             // Reference number / folio
  notas:       string | null;
}

// ─── Request types ────────────────────────────────────────────────────────────

export interface ExpenseCreate {
  fecha:        string;
  categoria:    ExpenseCategory;
  /**
   * Required for all categories.
   * When categoria = 'otro', explicitly required by UI validation
   * to provide meaningful context.
   */
  descripcion:  string;
  monto:        number;
  metodo_pago:  ExpensePaymentMethod;
  proveedor?:   string;
  comprobante?: string;
  notas?:       string;
}

export interface ExpenseUpdate extends Partial<ExpenseCreate> {}

export interface ExpenseFilters extends PaginationParams, DateRangeFilter, SortParams {
  categoria?:   ExpenseCategory;
  search?:      string;           // searches descripcion, proveedor
  metodo_pago?: ExpensePaymentMethod;
}

// ─── Summary (used by Dashboard) ─────────────────────────────────────────────

export interface ExpenseSummary {
  total: number;
  por_categoria: {
    categoria:   ExpenseCategory;
    label:       string;
    total:       number;
    porcentaje:  number;
  }[];
  periodo: {
    desde: string;
    hasta: string;
  };
}
